/**
 * Cache global des SETS d'OBJETS (artefacts/reliques) — référence publique.
 * Chargé une seule fois puis indexé par id, utilisé par le tooltip d'objet
 * (encart « Set ») et la Bibliothèque. Pendant de useComponentSetStore : même
 * forme (LibraryComponentSet), source distincte (/Library/item-sets).
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { fetchLibraryItemSets } from '@/api/library';
import type { LibraryComponentSet } from '@/types/library';

export const useItemSetStore = defineStore('itemSet', () => {
  const all = ref<LibraryComponentSet[]>([]);
  const byId = ref<Record<number, LibraryComponentSet>>({});
  const loaded = ref(false);
  let loading: Promise<void> | null = null;

  function ensureLoaded(): Promise<void> {
    if (loaded.value) return Promise.resolve();
    if (loading) return loading;
    loading = (async () => {
      try {
        const sets = await fetchLibraryItemSets();
        all.value = sets;
        byId.value = Object.fromEntries(sets.map((s) => [s.id, s]));
        loaded.value = true;
      } catch {
        /* non bloquant : pas d'encart de set si l'appel échoue */
      } finally {
        loading = null;
      }
    })();
    return loading;
  }

  /** Set par id (null si inconnu / pas encore chargé). */
  function get(id: number | null | undefined): LibraryComponentSet | null {
    return id != null ? (byId.value[id] ?? null) : null;
  }

  return { all, byId, loaded, ensureLoaded, get };
});
